/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { Inject } from '@odinjs/odin';
import { AutocompleteAPI, ButtonAPI, DatePickerAPI, DatePickerMode, DeckAPI, MapperAPI, ModalButton, ModalProps, ModalService, PanelAPI, SegmentedControlAPI, Tag, Text, TextAPI, Tooltip, TooltipTrigger, TrailAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnBeforeSubmit, OnChange, OnClick, OnDynamicContentRender, OnMount, OnSelectGroup, OnUnmount, OnUnselectGroup } from '@philips-internal/oswald-ui-core/lib/client';
import { HttpResponse, HttpService, MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { FragmentNames, PseudonymAppointmentsStep_7Aliases } from '../../codegen';
import TokenConstants from '../../constants/TokenConstants';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { BlockedItems, EstablishmenSuggestions, ScheduleItem, SuggestionGroups, SuggestionItems, SuggestionPeriodItem } from '../../interfaces/schedules-suggestions';
import { JWTTokenUtils } from '../../utils/jwt-token-utils';
import PseudonymAppointmentsBaseController from './pseudonym-appointments-base-controller';
import PseudonymAppointmentModel from './pseudonym-appointments-model';
import { AnotherPeriodClearReservation, ClearTimeReservation, CustomMessageRuleParameters, DesiredAppointmentDate, EstablishmentGroupLocalizationModel, FilterSuggestionRecord, FilterSuggestionType, PseudonymCustomMenssage, SchedulingConfig, Suggestions, TimeReservationResponse } from './pseudonym-interfaces';
import PseudonymSchedulingWaitingListService from './pseudonym-scheduling-waiting-list/pseudonym-scheduling-waiting-list-service';
import PseudonymIntegratedScheduleService from './pseudonym-service/pseudonym-integrated-schedule-service';
import { AppointmentsSteps, NO, ScheduleAvailableType, ScheduleType, TeleconsultationType, YES } from './pseudonym-types';
import { AbortableSubmitEvent, ChangeEvent, ClickEvent, MountEvent, RenderEvent, SelectItemEvent, SubmitEvent} from '@philips-internal/oswald-ui-core/lib/component/types';
import { ISO_DATE_FORMAT} from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import PseudonymSuggestionListModel from './pseudonym-suggestion/pseudonym-suggestion-list-model';
import { ContextType, Value } from '@philips-internal/oswald-ui-core';
import moment, { Moment as MomentType } from 'moment';
import { ButtonTheme, ModalButtonType } from '@philips-internal/oswald-ui-constants';
import { AppointmentUtils } from '../../utils/appointment-utils';
import React, { ReactElement } from 'react';
import { SubmittableFragmentContext } from '@philips-internal/oswald-ui-core/lib/component';
import { showToastNotificationInformation, showToastNotificationWarning } from '../../utils/toast-notification-utils';
import { generateUUID, isEmptyArrayOrObject, isMobile } from '@philips-internal/oswald-ui-utils';
import { FilterSuggestionOrders } from '../../app/types';
import { CUSTOM_MESSAGE_TYPE } from '../../constants/custom-message-type';
import PriceService, { Price } from './pseudonym-service/price.service';
import UserContext from '../../utils/user-context';

const FilterSuggestionOrders = new Map<string, FilterSuggestionOrders>([
  [FilterSuggestionType.AMOUNT, {
    description: 'tws.label-amount',
    id: FilterSuggestionType.AMOUNT,
  }],
  [FilterSuggestionType.DURATION, {
    description: 'tws.label-duration',
    id: FilterSuggestionType.DURATION,
  }],
  [FilterSuggestionType.TIME, {
    description: 'tws.label-time',
    id: FilterSuggestionType.TIME,
  }],
]);
@Controller({ component: PseudonymAppointmentsStep_7Aliases.LAYOUT_SUGGESTION, fragment: FragmentNames.PSEUDONYM_APPOINTMENTS_STEP_7 })
export default class PseudonymAppointmentsStep7Controller extends PseudonymAppointmentsBaseController {

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_NOTIFICATION_SUGGESTION)
    panelNotificationSuggestionApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_SUGGESTIONS)
    panelSuggestionApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_SUGGESTION_LIST)
    panelSuggestionListApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_SUGGESTION_GROUPS)
    panelSuggestionGroupsApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_CHOOSEN_ITEMS)
    panelChoosenItemsApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_CHOOSEN_ITEMS_TEXT)
    panelChoosenItemsTextApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.DATE_PICKER_SCHEDULE_RANGE)
    datePickerScheduleRange!: DatePickerAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_GROUPS)
    deckSuggestionGroupsApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_ITEMS)
    deckSuggestionGroupsItemsApi!: DeckAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.MAPPER_SUGGESTION_GROUPS)
    mapperSuggestionGroupsApi!: MapperAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_LOADER)
    panelLoaderApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.TRAIL_CHOOSEN_ITEMS)
    trailChoosenItemsApi!: TrailAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.TEXT_CHOOSEN_ITEMS)
    textChoosenItemsApi!: TextAPI;

  @Inject('PseudonymIntegratedScheduleService')
    pseudonymIntegratedScheduleService!: PseudonymIntegratedScheduleService;

  @Inject('PseudonymSchedulingWaitingListService')
    pseudonymSchedulingWaitingListService!: PseudonymSchedulingWaitingListService;

  @Inject('PriceService')
    priceService!: PriceService;

  @Inject(ContextType.SUBMITTABLE_FRAGMENT)
    submittableFragmentContext!: SubmittableFragmentContext;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_ANOTHER_PHYSICIAN)
    panelAnotherPhysician!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.TEXT_PHYSICIAN)
    textPhysicianApi!: TextAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.TEXT_SELECTED_DAYS)
    textSelectedDaysApi!: TextAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.BUTTON_ANOTHER_PERIOD)
    buttonAnotherPeriod!: ButtonAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.BUTTON_WAITING_LIST)
    buttonWaitingList!: ButtonAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.BUTTON_FIND_ANOTHER)
    buttonFindAnother!: ButtonAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_TEXT_PHYSICIAN)
    panelTextPhysicianApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.MAPPER_TRAIL)
    mapperTrailEstab!: MapperAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_ESTABLISHMENT)
    panelEstablishmentApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.PANEL_TITLE_ESTABLISHMENT)
    panelTitleEstablishmentApi!: PanelAPI;

  @Inject(PseudonymAppointmentsStep_7Aliases.SEGMENTED_CONTROL_ESTABLISHMENTS)
    segmentedControl0!: SegmentedControlAPI;

  private suggestionListModel!: PseudonymSuggestionListModel;
  private suggestionCardChoosenList!: Array<ScheduleItem>;
  private hasSegmentedControl = false;

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.PANEL_ESTABLISHMENT})
  handleMountPanelEstabl(event: MountEvent): void {
    if (!event) {
      return;
    }
    const suggestionGroups = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups;
    const shouldHideEstablishmentPanel =
      suggestionGroups.length > 1 ||
      !!suggestionGroups[0].blockedItems ||
      suggestionGroups[0].establishmenSuggestionsGroups.length === 0

    if (shouldHideEstablishmentPanel) {
      this.panelEstablishmentApi.setVisible(false);
      this.panelTitleEstablishmentApi.setVisible(false);
      this.panelTextPhysicianApi.setVisible(true);
      return;
    }

    const establishmentMap = this.setDataTrailMapper(suggestionGroups, suggestionGroups[0].establishmenSuggestionsGroups[0].value);
    const establishments = establishmentMap ? Array.from(establishmentMap.values()) : [];

    this.mapperTrailEstab.setData(establishments as unknown as Array<Record<string, Value>>);
  }

  @OnChange({ target: PseudonymAppointmentsStep_7Aliases.FIELD_SEGMENTED_CONTROL })
  handleClickSegmentedControl(event: ChangeEvent<EstablishmentGroupLocalizationModel[]>): void {
    if (!event) {
      return;
    }

    this.hasSegmentedControl = true;

    const nrSequencia = event.value.map(item => item.nrSequencia);
    const filterSuggestionRecord = event.value.map(item => item.nrSequencia) as unknown as EstablishmentGroupLocalizationModel;
    const establishment  = filterSuggestionRecord;
    let establishmentSuggestions = this.suggestionListModel.establishmentSuggestionsDefault;

    this.panelSuggestionApi.setVisible(false);
    this.panelLoaderApi.setVisible(true);
    try {
      if (establishment && nrSequencia.length > 0) {
        establishmentSuggestions = establishmentSuggestions.filter(
          establishmentSuggestion => Number(establishmentSuggestion.cdEstabelecimento) === Number(establishment)
        );
      }
    } catch {
      this.showToastNotificationLoadDataError();
    } finally {
      this.panelSuggestionApi.setVisible(true);
      this.panelLoaderApi.setVisible(false);
      this.suggestionListModel.establishmentSuggestions = establishmentSuggestions;
      PseudonymAppointmentModel.stepSeven.hasSelectedEstablishment = true;
      this.processWeekdays();
    }
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.LAYOUT_SUGGESTION })
  handleMountLayoutSuggestion(): void {
    this.panelNotificationSuggestionApi.setVisible(false);
    this.panelSuggestionListApi.setVisible(false);

    PseudonymAppointmentModel.stepSeven.unSelectAllCards = () => this.unSelectAllCards();
    PseudonymAppointmentModel.stepSeven.populateSuggestionTab = () => this.populateSuggestionTab();
    PseudonymAppointmentModel.stepSeven.refreshTrailChoosenItems = () => this.refreshTrailChoosenItems();
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.PANEL_SUGGESTIONS })
  handleOnMountPanelSuggestions(event: MountEvent): void {
    if (!event) {
      return;
    }

    const suggestionList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;
    this.suggestionCardChoosenList = this.getArrayScheduleItemCopy(suggestionList);
    const suggestionGroup = this.getFirstAvailableSuggestionGroup(PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups);
    if (!suggestionGroup) {
      return;
    }

    const establishmentSuggestions = suggestionGroup.establishmenSuggestionsGroups;
    const isShowPrice = suggestionGroup.isShowPrice;
    const numberTimesSchedulingDay = suggestionGroup.numberTimesSchedulingDay;
    this.suggestionListModel = new PseudonymSuggestionListModel(establishmentSuggestions, isShowPrice, numberTimesSchedulingDay, suggestionGroup);
    this.processWeekdays();
  }

  @OnMount(PseudonymAppointmentsStep_7Aliases.PANEL_CHOOSEN_ITEMS)
  async handleMountPanelChoosenItems(event: MountEvent): Promise<void> {

    if (!this.panelChoosenItemsApi.isVisible()) {
      return;
    }

    const panelApi = event.target as PanelAPI;
    const suggestionList = this.getArrayScheduleItemCopy(PseudonymAppointmentModel.stepSeven.suggestions.suggestionList);
    const suggestionGroups = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups;
    const suggestionBlockedList = this.getArrayScheduleItemCopy(PseudonymAppointmentModel.stepSeven.suggestions.blockedSuggestionList);
    const suggestionWaitingList = suggestionList.filter(item => this.filterSuggestionWaitingList(item));

    if (suggestionGroups?.length == 1 && suggestionWaitingList?.length === 0 && suggestionBlockedList?.length === 0) {
      panelApi.setVisible(false);
      return;
    }

    if (suggestionGroups?.length == 1 && suggestionWaitingList?.length == 1) {
      this.panelSuggestionGroupsApi.setVisible(false);
    }

    const firstAvailableSuggestionGroup = this.getFirstAvailableSuggestionGroup(PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups);
    this.setSuggestionGroupsMessageOnPanelChoosenItemsText(firstAvailableSuggestionGroup);
    if (firstAvailableSuggestionGroup) {
      this.updateDatePicker(firstAvailableSuggestionGroup.groupId, suggestionGroups);
      suggestionList.forEach(item => item.isSelected = item.groupId == firstAvailableSuggestionGroup.groupId);

      setTimeout(() => {
        if (firstAvailableSuggestionGroup?.establishmenSuggestionsGroups?.length > 0) {
          const orderedSuggestionGroup = firstAvailableSuggestionGroup.establishmenSuggestionsGroups.sort(
            (a, b) => moment(a.value).valueOf() - moment(b.value).valueOf()
          );
          this.datePickerScheduleRange.setValue(orderedSuggestionGroup[0].value);
          this.datePickerScheduleRange.setInitialDate(orderedSuggestionGroup[0].value);
        }
      });
    }

    if (suggestionBlockedList.length === 0 ) {
      this.trailChoosenItemsApi.setData(suggestionList as unknown as Record<string, Value>[]);
      return;
    }

    const blockedItemsMessage: Array<Promise<ScheduleItem>> = [];

    suggestionBlockedList.forEach(item => {
      const body = {
        action: CUSTOM_MESSAGE_TYPE.SCHEDULE_BLOCKING,
        insuranceCode: PseudonymAppointmentModel.stepOne.healthInsurance?.healthInsuranceId,
        internalProcedureNumber: item.nrSeqProcInterno || '',
        schedulingType: item.type,
        specialityCode: item.cdEspecialidade || '',
      } as CustomMessageRuleParameters;

      const customMessage = HttpService.get(APPLICATION_ENDPOINTS.GET_WSUITE_CUSTOM_MESSAGE, { ...body })
        .then(response => {
          const pseudonymCustomMenssage = response.body as PseudonymCustomMenssage;
          item.isBlocked = true;
          item.customMessage = pseudonymCustomMenssage?.message;
          return item;
        }).catch(() => {
          return item;
        });

      blockedItemsMessage.push(customMessage);
    });

    Promise.all(blockedItemsMessage)
      .then((blockedItem) => {
        blockedItem.forEach(e => suggestionList.push(e));
      }).finally(() => {
        this.trailChoosenItemsApi.setData(suggestionList as unknown as Record<string, Value>[]);
      });
  }

  @OnMount(PseudonymAppointmentsStep_7Aliases.PANEL_ANOTHER_PHYSICIAN)
  handleOnMountPanelAnotherPhysician(): void {
    const suggestionList = this.getArrayScheduleItemCopy(PseudonymAppointmentModel.stepSeven.suggestions.suggestionList);
    const suggestionGroups = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups;
    const hasEmptyEstablishmentGroups = PseudonymAppointmentModel.findAnotherPhysician.emptyEstablishmentGroups;
    const suggestionWaitingList = suggestionList.filter(item => this.filterSuggestionWaitingList(item));
    const group = suggestionGroups?.[0];
    const scheduleItem = group?.scheduleItems?.[0];
    const blockedItem = group?.blockedItems?.[0];
    const physician = scheduleItem?.cdMedico ?? blockedItem?.cdMedico;
    const especiality = scheduleItem?.cdEspecialidade ?? blockedItem?.cdEspecialidade;
    const isSingleGroup = suggestionGroups?.length === 1;
    const isConsultation = scheduleItem?.type === 'C' || blockedItem?.ieTipoAgendamento === 'C';

    let showButtonWaitingList = false;

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const shouldShowFindAnother = () => {
      const hasBlockedItems = !!group?.blockedItems;
      return (
        (hasEmptyEstablishmentGroups || hasBlockedItems) &&
      isSingleGroup &&
      isConsultation &&
      physician &&
      especiality
      );
    };

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    const shouldShowPanelTextPhysician = () => (
      hasEmptyEstablishmentGroups &&
      isSingleGroup &&
      isConsultation &&
      scheduleItem?.cdMedico &&
      scheduleItem?.cdEspecialidade
    );

    if (isSingleGroup && suggestionWaitingList.length > 0 && isConsultation) {
      this.panelChoosenItemsApi.setVisible(false);
      showButtonWaitingList = true;
    }

    this.buttonAnotherPeriod.setVisible(true);

    if (showButtonWaitingList && isSingleGroup) {
      this.buttonWaitingList.setVisible(true);
    }

    if (shouldShowFindAnother()) {
      this.buttonFindAnother.setVisible(true);
      if (blockedItem) {
        PseudonymAppointmentModel.findAnotherPhysician.emptySuggestions = [blockedItem as unknown as ScheduleItem];
      }
    }
    PseudonymAppointmentModel.stepSeven.isMessageDefault = !shouldShowPanelTextPhysician();
    if (shouldShowPanelTextPhysician()) {
      this.panelTextPhysicianApi.setVisible(true);
    }
    PseudonymAppointmentModel.findAnotherPhysician.emptyEstablishmentGroups = false;
  }

  @OnMount(PseudonymAppointmentsStep_7Aliases.TEXT_PHYSICIAN)
  handleOnMountTextPhysician(event: MountEvent): void {
    if (!event) {
      return;
    }
    let msg = MessageService.translate('patient.appointment.without-time').concat('.');
    if (!PseudonymAppointmentModel.stepSeven.isMessageDefault) {
      const suggestionGroups = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups;
      const physician = suggestionGroups?.[0]?.scheduleItems?.[0]?.dsProcInterno;
      msg = MessageService.translate('pseudonym-appointments.text-physician').replace('@#MEDICO@#', physician);
    }
    this.textPhysicianApi.setValue(msg);
  }

  @OnMount(PseudonymAppointmentsStep_7Aliases.TEXT_SELECTED_DAYS)
  handleOnMountTextSelectedDays(event: MountEvent): void {
    if (!event) {
      return;
    }
    const date = this.context.get('dateRange') as Record<string, string>;
    let startDate = date.start;
    let endDate = date.end;

    if (PseudonymAppointmentModel.stepAnotherPeriod.isAnotherPeriodSelected) {
      startDate = PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.start;
      endDate = PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.end;
      PseudonymAppointmentModel.stepAnotherPeriod.isAnotherPeriodSelected = false;
    }

    let msg = MessageService.translate('pseudonym-appointments.period-selected');
    msg = msg.replace('#@DT_INICIAL#@', moment.utc(startDate).format('DD/MM/YYYY')).replace('#@DT_FINAL#@', moment.utc(endDate).format('DD/MM/YYYY'));
    this.textSelectedDaysApi.setValue(msg);
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.DATE_PICKER_SCHEDULE_RANGE })
  handleOnMountDatePickerSelect(event: MountEvent): void {
    if (!event) {
      return;
    }

    if (isMobile() && PseudonymAppointmentModel.findAnotherPhysician.emptyEstablishmentGroups) {
      this.datePickerScheduleRange.setMode(DatePickerMode.WEEK);
    }
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_GROUPS })
  handleOnMountDeckSuggestionGroups(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    deck.setDone(true);
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_ITEMS })
  handleOnMountDeckSuggestionItems(event: MountEvent): void {
    const deck = event.target as DeckAPI;
    const suggestionItems = deck.getData() as unknown as Array<SuggestionItems>;
    const identifier = suggestionItems[0]?.suggestionItems[0]?.suggestionGroupId;
    const hasItem = PseudonymAppointmentModel.stepSeven.suggestionSelected[0]?.suggestionItems[0]?.suggestionId ?? undefined;

    if (!this.suggestionListModel.hasSelected) {
      deck.unselectAll();
    }

    deck.setDone(true);
    if (identifier) {
      const lastSuggestion = this.suggestionListModel.decksSuggestionItems.find(item => item == deck);
      if (!lastSuggestion) {
        this.suggestionListModel.decksSuggestionItems[identifier] = deck;
      }
    }
    setTimeout(() => {
      const deckData = deck.getData() as unknown as Array<SuggestionItems>;
      if (hasItem && deckData) {
        const selected = deckData.some(item =>
          item.suggestionItems.some(suggestionItem => suggestionItem.suggestionSelected)
        );
        if (selected) {
          this.suggestionListModel.lastDeckApi = deck;
          this.suggestionListModel.hasSelected = true;
          deck.selectGroup(String(hasItem));
        }
      }
    }, 0);
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.AUTOCOMPLETE_ESTABLISHMENT })
  handleOnMountAutocompleteEstablishment(event: MountEvent): void {
    const autocomplete = event.target as AutocompleteAPI;
    const suggestionGroup = this.suggestionListModel.suggestionGroup;
    const establishmentsSuggestions = suggestionGroup.establishmenSuggestionsGroups;
    const establishments: Array<EstablishmentGroupLocalizationModel> = [];

    establishmentsSuggestions.forEach(establishment => {
      const hasItem = establishments.find(item => item.nrSequencia === establishment.cdEstabelecimento);
      if (!hasItem) {
        establishments.push({
          dsGrupoEstab: establishment.dsEstablishment,
          nrSequencia: establishment.cdEstabelecimento,
        });
      }
    });

    autocomplete.setData(establishments as unknown as Array<Record<string, Value>>);
  }

  @OnMount({ target: PseudonymAppointmentsStep_7Aliases.AUTOCOMPLETE_SORT })
  handleOnMountAutocompleteSort(event: MountEvent): void {
    const autocomplete = event.target as AutocompleteAPI;
    const filterOrders = FilterSuggestionOrders;
    const isShowPrice = this.suggestionListModel.isShowPrice;
    const isShowDuration = JWTTokenUtils.getClaimValue(TokenConstants.SHOW_SCHEDULING_DURATION) === YES;

    if (!isShowDuration) {
      filterOrders.delete(FilterSuggestionType.DURATION);
    }
    if (!isShowPrice) {
      filterOrders.delete(FilterSuggestionType.AMOUNT);
    }

    autocomplete.setData([...filterOrders.values()] as unknown as Array<Record<string, Value>>);
  }

  @OnBeforeSubmit({ target: PseudonymAppointmentsStep_7Aliases.FILTER_SUGGESTION_TIMES })
  handleOnBeforeSubmitFilterSuggestionsTimes(event: AbortableSubmitEvent): void {
    const filterSuggestionRecord = event.record as FilterSuggestionRecord;
    const { establishment, ordenation, onlyTeleconsultation } = filterSuggestionRecord;
    let establishmentSuggestions = this.suggestionListModel.establishmentSuggestionsDefault;

    this.panelSuggestionApi.setVisible(false);
    this.panelLoaderApi.setVisible(true);
    try {
      if (establishment) {
        establishmentSuggestions = establishmentSuggestions.filter(establishmentSuggestion => establishmentSuggestion.cdEstabelecimento === establishment.nrSequencia);
        if (!ordenation) {
          establishmentSuggestions = this.getSuggestionsOrdered(establishmentSuggestions, FilterSuggestionType.TIME);
        }
      }

      if (onlyTeleconsultation) {
        establishmentSuggestions = this.doTeleconsultationFilter(establishmentSuggestions, onlyTeleconsultation);
      }

      if (ordenation) {
        establishmentSuggestions = this.getSuggestionsOrdered(establishmentSuggestions, ordenation.id);
      }
    } catch {
      this.showToastNotificationLoadDataError();
    } finally {
      this.panelSuggestionApi.setVisible(true);
      this.panelLoaderApi.setVisible(false);

      this.suggestionListModel.establishmentSuggestions = establishmentSuggestions;
      this.processWeekdays();
    }
  }

  @OnUnmount({ target: PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_ITEMS })
  handleUnMountDeckSuggestionItems(event: MountEvent): void {
    if (!event) {
      return;
    }
    const deck = event as unknown as DeckAPI;
    if (deck?.selectGroup?.length > 0) {
      if (this.hasSegmentedControl && this.suggestionListModel.hasSelected) {
        this.suggestionListModel.hasSelected = true;
      } else {
        this.suggestionListModel.hasSelected = false;
      }
    }
  }

  @OnClick(PseudonymAppointmentsStep_7Aliases.PSEUDONYM_SUGGESTION_CARD_ITEM)
  handleClickCardDeckChoosenItems(event: ClickEvent): void {
    if (!event?.value) {
      return;
    }

    const selectedCardGroups = event.value as ScheduleItem;
    if (selectedCardGroups.groupId === this.suggestionListModel.suggestionGroup.groupId){
      return;
    }

    if (this.panelSuggestionGroupsApi.isVisible()) {
      this.deckSuggestionGroupsApi.setData([]);
      this.deckSuggestionGroupsApi.setDone(true);
    }

    this.panelSuggestionGroupsApi.setVisible(false);

    const choosenItemsData = this.trailChoosenItemsApi?.getData() as unknown as Array<ScheduleItem>;
    this.updateSelectedCardChoosenItems(choosenItemsData, selectedCardGroups.nrSeqAgeintItem);

    const suggestionGroup = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups[selectedCardGroups.groupId];
    const establishmentSuggestions = suggestionGroup.establishmenSuggestionsGroups;
    const isShowPrice = suggestionGroup.isShowPrice;
    const numberTimesSchedulingDay = suggestionGroup.numberTimesSchedulingDay;
    this.suggestionListModel = new PseudonymSuggestionListModel(establishmentSuggestions, isShowPrice, numberTimesSchedulingDay, suggestionGroup);

    if (isEmptyArrayOrObject(this.suggestionListModel.establishmentSuggestions)) {
      this.panelSuggestionGroupsApi.setVisible(false);
      this.datePickerScheduleRange.clearRanges();
      return;
    }

    if (suggestionGroup) {
      this.setSuggestionGroupsMessageOnPanelChoosenItemsText(suggestionGroup);
    }

    this.panelSuggestionGroupsApi.setVisible(true);

    this.insertRangeIntoDatePicker(establishmentSuggestions);
    const datePickValue = this.datePickerScheduleRange.getInitialDate() as string;
    this.updateDataOnMapperSuggestionGroups(datePickValue);
  }

  @OnClick({ target: PseudonymAppointmentsStep_7Aliases.BUTTON_ANOTHER_PERIOD })
  handleClickLinkAnotherPeriod(event: ClickEvent): void {
    if (!event) {
      return;
    }

    const modalId = 'another-period-modal';

    PseudonymAppointmentModel.stepSeven.multiSelected.forEach(() => {
      PseudonymAppointmentModel.stepSeven.hasSelected = true;
    });
    if (PseudonymAppointmentModel.stepSeven.multiSelected.length === 0) {
      PseudonymAppointmentModel.stepSeven.hasSelected = false;
    }

    if (this.suggestionListModel.hasSelected || PseudonymAppointmentModel.stepSeven.hasSelected || PseudonymAppointmentModel.stepSeven.suggestionSelected.length > 0) {
      const dialogButtons: ModalButton[] = [
        {
          action: () => undefined,
          title: 'patient.cancel',
          type: ModalButtonType.CANCEL,
        },
        {
          action: () => {
            const selectedItem = this.suggestionListModel.suggestionItemsSelected;
            const selectedItemList = selectedItem.flatMap(item => item.suggestionItems);
            const clearTimeReservationList = selectedItemList.map(item => ({
              nrSeqAgeintItem: Number(item.nrSeqAgeintItem)
            }));
            if (PseudonymAppointmentModel.stepSeven.multiSelected.length > 0) {
              PseudonymAppointmentModel.stepSeven.multiSelected.forEach(item => {
                clearTimeReservationList.push({
                  nrSeqAgeintItem: Number(item.nrSeqAgeintItem)
                });
              });
            }
            this.AnotherPeriodClearReservation(
              clearTimeReservationList.map(item => ({
                nrSeqAgeint: PseudonymAppointmentModel.integratedScheduleSequence,
                nrSeqAgeintItem: item.nrSeqAgeintItem
              }))
            );
            PseudonymAppointmentModel.stepSeven.hasSelected = false;
            PseudonymAppointmentModel.stepSeven.multiSelected = [];
            PseudonymAppointmentModel.stepSeven.suggestionSelected = [];
            this.unSelectAllCards();
            this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);
            ModalService.close(props.uuid);
            this.openAnotherPeriodModal(modalId);
          },
          title: 'patient.continue',
          type: ModalButtonType.SUCCESS,
        }
      ];

      const props: ModalProps = {
        closeable: false,
        closeByDocument: false,
        closeByEscape: false,
        dialogDescription: 'new-appointment.another-period-information',
        dockButtons: dialogButtons,
        focusOnOpen: true,
        title: 'new-appointment.another-period-confirmation',
        uuid: generateUUID(),
      };

      ModalService.openDialog(props);

    } else {
      this.openAnotherPeriodModal(modalId);
    }

    this.context.set({isAnotherPeriod: true} as Record<string, Value>);
  }

  @OnClick({ target: PseudonymAppointmentsStep_7Aliases.BUTTON_FIND_ANOTHER })
  handleClickButtonFindAnother(event: ClickEvent): void {
    if (!event) {
      return;
    }
    if (PseudonymAppointmentModel.stepTwo.procedures[0].professional) {
      PseudonymAppointmentModel.stepTwo.procedures[0].professional = '';
    }
    PseudonymAppointmentModel.stepTwo.hasProcedureChange = true;
    PseudonymAppointmentModel.stepAnotherPeriod.findAnotherPhysician = true;
    this.populateSuggestionTab();
  }

  @OnClick({ target: PseudonymAppointmentsStep_7Aliases.BUTTON_WAITING_LIST })
  async handleClickButtonWaitingList(event: ClickEvent): Promise<void> {
    if (!event) {
      return;
    }
    const item = this.suggestionCardChoosenList.find(value => String(value.nrSeqAgeintItem))
    this.showPseudonymWaitingListModal(item?.nrSeqAgeintItem);
  }

  @OnChange({ target: PseudonymAppointmentsStep_7Aliases.DATE_PICKER_SCHEDULE_RANGE })
  handleOnChangeDatePickerSelect(event: ChangeEvent<string>): void {
    if (!event?.value) {
      return;
    }

    this.updateDataOnMapperSuggestionGroups(event.value);
    if (this.panelEstablishmentApi.isVisible()) {
      const formattedDate = moment.utc(event.value).format('YYYY-MM-DDT00:00:00');
      this.mapperTrailEstab.setData(this.setDataTrailMapper(
        [this.suggestionListModel.suggestionGroup],
        formattedDate
      ) as unknown as Array<Record<string, Value>>);
      PseudonymAppointmentModel.stepSeven.selectedDateEstablishment = event.value;
    }
  }

  @OnUnselectGroup({ target: PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_ITEMS })
  handleOnUnSelectGroupDeckSuggestionItems(event: SelectItemEvent<Record<string, Value>[]>): void {
    if (!event || !this.suggestionListModel.hasSelected) {
      return;
    }

    const selectedItem = event.item as unknown as Array<SuggestionItems>;
    const selectedItemList = selectedItem.flatMap(item => item.suggestionItems);
    const suggestionGroups = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups;

    const clearTimeReservationList = selectedItemList.map(item => ({
      nrSeqAgeintItem: Number(item.nrSeqAgeintItem)
    }));
    this.clearTimeReservation(clearTimeReservationList);

    this.suggestionListModel.hasSelected = false;
    this.suggestionListModel.wantToRequest = false;
    this.suggestionListModel.lastDeckApi = undefined;
    this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);

    const groupId = this.suggestionListModel.suggestionGroup.groupId as unknown as string;

    if (suggestionGroups?.length > 1) {
      this.removeItemPseudonymSelected(groupId);
    }

    PseudonymAppointmentModel.stepSeven.suggestionSelected = [];
  }

  @OnSelectGroup({ target: PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_ITEMS })
  handleOnSelectGroupDeckSuggestionItems(event: SelectItemEvent<Record<string, Value>[]>): void {
    if (!event) {
      return;
    }

    const deckApi = event.target as unknown as DeckAPI;
    const selectedItems = event.item as unknown as Array<SuggestionItems>;
    const lastDeckApi = this.suggestionListModel.lastDeckApi;
    let hasSelected = this.suggestionListModel.hasSelected;

    if (selectedItems.length === 0) {
      return;
    }

    PseudonymAppointmentModel.stepSeven.suggestionSelected = selectedItems;
    selectedItems.forEach(item => {
      item.suggestionItems.forEach(suggestionItem => {
        suggestionItem.suggestionSelected = true;
        suggestionItem.type = suggestionItem.appointmentType ? suggestionItem.appointmentType : '';
      });
      item.hasSchedulingTimes = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups[0].establishmenSuggestionsGroups.length > 0;
    });

    const canSelect = this.checkCanSelectByType(selectedItems);

    const unselectLastDeck = () => {
      if (lastDeckApi) {
        lastDeckApi.unselectAll();
        const lastDeckData = lastDeckApi.getData() as unknown as Array<SuggestionItems> | undefined;
        if (lastDeckData?.[0]?.suggestionItems?.[0]) {
          lastDeckData[0].suggestionItems.forEach(items => {
            items.suggestionSelected = false;
          });
        }
      }
    };

    const selectCurrentDeck = (identifierGroup: string) => {
      deckApi.selectGroup(identifierGroup);
    };

    if (canSelect) {
      const { suggestionId: identifierGroup, suggestionGroupId: deckIndex } = selectedItems[0].suggestionItems[0];
      const suggestionsHybrid = selectedItems[0].suggestionItems
        .filter(suggestion => suggestion.teleconsultationType === ScheduleAvailableType.HIBRID);

      let isValid = false;
      try {
        isValid = !!(lastDeckApi && typeof lastDeckApi.getData === 'function' && lastDeckApi.getData());
      } catch {
        isValid = false;
      }

      if (lastDeckApi && isValid && hasSelected) {
        unselectLastDeck();
        selectCurrentDeck(String(identifierGroup));
      } else {
        hasSelected = true;
      }

      this.suggestionListModel.lastDeckApi = this.suggestionListModel.decksSuggestionItems[deckIndex];

      this.selectedSuggestion(hasSelected, selectedItems, deckApi, suggestionsHybrid);
    } else {
      hasSelected = false;
      deckApi.unselectAll();

      const suggestionItemsSelected = this.suggestionListModel.suggestionItemsSelected;
      if (lastDeckApi && suggestionItemsSelected?.length > 0) {
        const lastDeckSelected = suggestionItemsSelected[0].suggestionId;
        lastDeckApi.selectGroup(String(lastDeckSelected || ''));
        hasSelected = Boolean(lastDeckSelected);
      }
    }

    this.suggestionListModel.hasSelected = hasSelected;
  }

  @OnDynamicContentRender({ target: PseudonymAppointmentsStep_7Aliases.DECK_SUGGESTION_ITEMS })
  handleOnDynamicContentRenderDeckSuggestionItems(event: RenderEvent): void {
    if (!event) {
      return;
    }

    const render = event.render;
    const currentDeckAPI = event.target as DeckAPI;
    const suggestionsItems = event.data as Array<SuggestionItems>;
    const schedulePrice = { currencySymbol: '', total: 0 } as Price;
    const copagoPrice = { currencySymbol: '', total: 0 } as Price;
    const canSelected = this.priceService.calculatePrice(suggestionsItems, schedulePrice, copagoPrice);

    const renderSuggestionDate = this.generateSuggestionItemsReactElement(currentDeckAPI, suggestionsItems, schedulePrice, canSelected, copagoPrice);
    render(renderSuggestionDate);
  }

  private generateSuggestionItemsReactElement(
    currentDeckAPI: DeckAPI,
    suggestionsItems: Array<SuggestionItems>,
    schedulePrice: Price,
    canSelected: boolean,
    copagoPrice: Price
  ): () => ReactElement {
    const suggestionItem = suggestionsItems[0];
    const isShowTotal = AppointmentUtils.isShowTotal(suggestionItem?.suggestionItems || []);
    const totalLabel = this.priceService.getValueToDisplay(schedulePrice, this.priceService.Label.SCHEDULE_TOTAL);
    const copagoLabel = this.priceService.getValueToDisplay(copagoPrice, this.priceService.Label.COPAGO);
    const viewOnlyClass = `${!canSelected ? 'deck-title-suggestion-item--no-select' : 'deck-title-suggestion-item'}`;
    const oswaldDeckHeaderClass = 'c-deck__group-header-default-content';
    const oswaldDeckTitleClass = 'c-deck__group-header-title';
    const oswaldDeckTitleText = `${MessageService.translate('tws.appointment-suggestion.suggestion')} ${suggestionItem.suggestionId}`;

    return canSelected
      ? this.generateSuggestionDateCanSelectedReactElement(currentDeckAPI, isShowTotal, totalLabel,oswaldDeckHeaderClass, oswaldDeckTitleClass, oswaldDeckTitleText, copagoLabel)
      : this.generateSuggestionDateReactElement(isShowTotal, totalLabel, oswaldDeckHeaderClass, viewOnlyClass, oswaldDeckTitleClass, oswaldDeckTitleText, copagoLabel);
  }

  private generateSuggestionDateCanSelectedReactElement(
    currentDeckAPI: DeckAPI, isShowTotal: boolean, totalLabel: string,
    oswaldDeckHeaderClass: string, oswaldDeckTitleClass: string, oswaldDeckTitleText: string,
    copagoLabel : string
  ): () => ReactElement {
    const renderSuggestionDateCanSelected: () => ReactElement = () => {
      return (
        <>
          <div className="wrapper-full-click" onClick={currentDeckAPI.onClick.fire}></div>
          <div className={`${oswaldDeckHeaderClass} deck-title-suggestion-item`}>
            <div className={oswaldDeckTitleClass}>
              <Text value={oswaldDeckTitleText} />
            </div>
            {this.suggestionListModel.isShowPrice && isShowTotal &&
              <div className='price'>
                <Text value={totalLabel} />
              </div>}
            {copagoLabel != '' &&
              <div className='price'>
                <Text value={copagoLabel} />
              </div>}
          </div>
        </>
      );
    }
    return renderSuggestionDateCanSelected;
  }

  private generateSuggestionDateReactElement(
    isShowTotal: boolean, totalLabel: string,
    oswaldDeckHeaderClass: string, viewOnlyClass: string, oswaldDeckTitleClass: string, oswaldDeckTitleText: string,
    copagoLabel: string
  ): () => ReactElement {
    const renderSuggestionDate: () => ReactElement = () => {
      return (
        <div className={`${oswaldDeckHeaderClass} ${viewOnlyClass}`}>
          <div className={oswaldDeckTitleClass}>
            <Text value={oswaldDeckTitleText} />
          </div>
          <Tooltip
            triggers={[TooltipTrigger.HOVER]}
            value={MessageService.translate('patient.tooltip.visualisation')}
            multiline={true}>
            <div className='tooltip-custom__target'>
              <Tag
                value={MessageService.translate('patient.view-only')}
                formatters={{}}
              />
            </div>
          </Tooltip>
          {this.suggestionListModel.isShowPrice && isShowTotal &&
            <div className='price'>
              <Text value={totalLabel} />
            </div>
          }
          {copagoLabel != '' &&
            <div className='price'>
              <Text value={copagoLabel} />
            </div>
          }
        </div>
      );
    }
    return renderSuggestionDate;
  }

  private processWeekdays(): void {
    const dateFilterItens: Array<Record<string, Value>> = [];
    const establishmentSuggestions = this.suggestionListModel.establishmentSuggestions;

    if (!establishmentSuggestions || establishmentSuggestions.length === 0) {
      this.changePanelSuggestionGroupsApi(false);
      return;
    }

    establishmentSuggestions.forEach((suggestion) => {
      const date = dateFilterItens.find(item => item.date === suggestion.value);

      if (!date) {
        dateFilterItens.push({
          date: suggestion.value,
          id: suggestion.value,
          value: moment(suggestion.value).format('dddd, DD/MM/YYYY')
        });
      }
    });

    const dateFilterItensSorted = dateFilterItens.slice().sort((dateA, dateB) => {
      const dateAParsed = moment(dateA.date as string);
      const dateBParsed = moment(dateB.date as string);
      return dateAParsed.diff(dateBParsed);
    });

    if (dateFilterItensSorted.length === 0) {
      this.panelSuggestionGroupsApi.setVisible(true);
      return;
    }

    let initialDate = dateFilterItensSorted[0].date as unknown as string;
    if (PseudonymAppointmentModel.stepSeven.hasSelectedEstablishment) {
      initialDate = PseudonymAppointmentModel.stepSeven.selectedDateEstablishment || dateFilterItensSorted[0].date as unknown as string;
    }

    this.insertRangeIntoDatePicker(establishmentSuggestions);
    this.updateDataOnMapperSuggestionGroups(initialDate);
  }

  private updateDataOnMapperSuggestionGroups(currentDate: string): void {
    const establishmentSuggestionsModel = this.suggestionListModel.establishmentSuggestions;
    if (!establishmentSuggestionsModel || establishmentSuggestionsModel.length === 0) {
      this.changePanelSuggestionGroupsApi(false);
      return;
    }

    let initialDate = moment(currentDate, ISO_DATE_FORMAT);
    if (this.datePickerScheduleRange.getRanges().length === 1) {
      this.datePickerScheduleRange.setValue([this.datePickerScheduleRange.getRanges()[0].startDate]);
      this.datePickerScheduleRange.setInitialDate(this.datePickerScheduleRange.getRanges()[0].startDate);
      initialDate = moment(this.datePickerScheduleRange.getRanges()[0].startDate, ISO_DATE_FORMAT);
    } else {
      this.datePickerScheduleRange.setValue(initialDate.utc().toISOString());
      this.datePickerScheduleRange.setInitialDate(initialDate.utc().toISOString());
    }

    if (this.hasSegmentedControl && this.suggestionListModel.hasSelected) {
      this.suggestionListModel.hasSelected = true;
    } else {
      this.suggestionListModel.hasSelected = false;
    }

    this.panelSuggestionGroupsApi.setVisible(true);
    setTimeout(() => {
      const segmentedDate = initialDate.toString();
      const establishmentSuggestions = establishmentSuggestionsModel
        .filter(suggestion => moment(suggestion.value, ISO_DATE_FORMAT).isSame(segmentedDate))
        .slice(0, this.suggestionListModel.numberTimesSchedulingDay);

      establishmentSuggestions.forEach((establishmentSuggestion, index) => {
        establishmentSuggestion.suggestions.forEach(suggestionItems => {
          suggestionItems.suggestionItems.forEach(suggestion => {
            suggestion.suggestionGroupId = index + 1;
            suggestion.dsEstablishment = establishmentSuggestion.dsEstablishment;
            suggestion.cdEstabelecimento = establishmentSuggestion.cdEstabelecimento;
            suggestion.isShowPrice = this.suggestionListModel.isShowPrice;
            if (PseudonymAppointmentModel.stepSeven.suggestionSelected[0] !== undefined
               && Number(suggestion.nrSeqAgenda) === Number(PseudonymAppointmentModel.stepSeven.suggestionSelected[0].suggestionItems[0].nrSeqAgenda)) {
              suggestion.suggestionSelected = true;
            } else {
              suggestion.suggestionSelected = false;
            }
          });
        });
      });

      this.mapperSuggestionGroupsApi.setData(establishmentSuggestions as unknown as Array<Record<string, Value>>);
    });
  }

  private openDialogHasConfirmTypeConsultation(): void {
    const suggestionItemsSelected = this.suggestionListModel.suggestionItemsSelected;
    ModalService.openDefault({
      closeable: false,
      dockButtons: [
        {
          action: () => { },
          theme: ButtonTheme.SECONDARY,
          title: 'patient.cancel',
          type: ModalButtonType.CANCEL,
        },
        {
          action: () => { },
          title: 'patient.burron.confirm',
          type: ModalButtonType.SUCCESS,
        }
      ],
      fragment: FragmentNames.PSEUDONYM_SUGGESTION_TYPE_CONSULTATION,
      onCancel: () => {
        this.unSelectAllCards();
      },
      onSubmit: (event) => {
        suggestionItemsSelected[0].suggestionItems.forEach(suggestion => {
          if (suggestion.teleconsultationType === ScheduleAvailableType.HIBRID) {
            suggestion.suggestionTypeConsultation = event.record.suggestionTypeConsultation.value;
          }
        });
        this.verifyIfCanHandleConfirmSuggestion();
      },
      title: 'text.choose-an-option-below',
    });
  }

  private checkCanSelectByType(selectedItems: Array<SuggestionItems>): boolean {
    const suggestionItems = selectedItems[0].suggestionItems;
    const canSelect = suggestionItems.filter(suggestion => suggestion.availableType === ScheduleAvailableType.READ_ONLY).length === 0;
    return canSelect;
  }

  private async setSelectedSuggestion(groupSequence: number, selectedList: Array<ScheduleItem> = []): Promise<void> {
    if (!selectedList?.length) {
      return Promise.resolve();
    }

    const isReservationRegistered = await this.registerTimeSchedule(groupSequence, selectedList);

    if (!isReservationRegistered) {
      PseudonymAppointmentModel.stepSeven.isLastSuggestionDenied = true;
      return Promise.resolve();
    }

    let allSelected = true;
    const schedulePrice = { currencySymbol: '', total: 0 } as Price;
    const suggestionList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;
    const selectedLista: ScheduleItem[] = [];
    suggestionList.forEach(item => {
      const selectedDate = selectedList.find(suggestion => {
        return item.nrSeqAgeintItem.toString() === suggestion.nrSeqAgeintItem.toString();
      });

      if (selectedDate) {
        this.populateScheduleItem(selectedDate, item, groupSequence);
      } else {
        const scheduleWaitingListDataArray = PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray;
        item.isWaitingListItem = scheduleWaitingListDataArray.some(row => Number(row.nrSeqAgeintItem) === Number(item.nrSeqAgeintItem));
        item.showWaitingListModal = this.showPseudonymWaitingListModal.bind(this);
        item.removeItemFromWaitingList = this.removeItemPseudonymWaitingList.bind(this);
      }

      this.priceService.getTotalPrice(item.price, item.priceNoFormatted, schedulePrice, true, item.ieReturn);
      selectedLista.push(item);
      if (!item.selectedDate && item.hasSchedulingTimes) {
        allSelected = false;
      }
    });

    this.context.set({
      suggestionList: selectedLista as unknown as Record<string, Value>[],} as Record<string, Value>);

    this.updateCardsChoosenItems();
    this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, allSelected);
    PseudonymAppointmentModel.stepSeven.hasSelected = true;
    return Promise.resolve();
  }

  private async registerTimeSchedule(groupSequence: number, selectedList: Array<ScheduleItem>): Promise<boolean> {
    try {
      await this.checkAndClearTimeReservation(selectedList);
      let isReservationRegistered = true;
      const checkTimeReservationPromises: Array<Promise<boolean>> = [];

      selectedList.forEach(suggestion => {
        const promise = this.checkTimeReservation(suggestion, groupSequence)
          .then((response) => isReservationRegistered = response);
        checkTimeReservationPromises.push(promise);
      });
      try {
        await Promise.all(checkTimeReservationPromises);
        return isReservationRegistered;
      } catch {
        return false;
      }
    } catch {
      this.showToastNotificationLoadDataError();
      return false;
    }
  }

  private checkAndClearTimeReservation(newSelectedList: Array<ScheduleItem>): Promise<HttpResponse<void>> {
    const clearTimeReservationList: Array<ClearTimeReservation> = [];
    const suggestionList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;

    newSelectedList.forEach(newItem => {
      const itemToRemove = suggestionList.filter(item =>
        Number(item.nrSeqAgeintItem) === Number(newItem.nrSeqAgeintItem) &&
        item.availableType !== ScheduleAvailableType.REQUEST && newItem.availableType === ScheduleAvailableType.REQUEST
      );

      if (itemToRemove.length === 1) {
        clearTimeReservationList.push({ nrSeqAgeintItem: Number(itemToRemove[0].nrSeqAgeintItem) });
      }
    });

    if (clearTimeReservationList.length > 0) {
      return this.clearTimeReservation(clearTimeReservationList);
    }

    return Promise.resolve({} as HttpResponse<void>);
  }

  private clearTimeReservation(clearTimeReservationList: Array<ClearTimeReservation>): Promise<HttpResponse<void>> {
    return HttpService.delete(APPLICATION_ENDPOINTS.CLEAR_TIME_RESERVATION,
      { patientId: PseudonymAppointmentModel.stepOne.currentPatientSelected.id },
      { body: clearTimeReservationList as unknown as Record<string, Value> }
    );
  }

  private AnotherPeriodClearReservation(clearTimeList: Array<AnotherPeriodClearReservation>): Promise<HttpResponse<void>> {
    return HttpService.delete(APPLICATION_ENDPOINTS.ANOTHER_PERIOD_CLEAR_TIME_RESERVATION,
      { patientId: UserContext.patientId },
      { body: clearTimeList as unknown as Record<string, Value> }
    );
  }

  private async checkTimeReservation(suggestion: ScheduleItem, groupSequence: number): Promise<boolean> {
    const ACTION_INSERT = 'I';
    const suggestionGroups = PseudonymAppointmentModel.stepSeven.suggestions.suggestionGroups;
    const body = {
      cdAgenda: suggestion.agendaId,
      cdMedico: suggestion.cdMedico,
      hrAgenda: suggestion.startDate,
      ieAcao: ACTION_INSERT,
      nrMinutoDuracao: suggestion.nrMinutoDuracao,
      nrSeqAgeint: suggestionGroups[groupSequence].nrSeqAgeint,
      nrSeqAgeintItem: suggestion.nrSeqAgeintItem,
      nrSeqAgenda: suggestion.nrSeqAgenda,
      nrSeqCombo: suggestion.suggestionId,
      qtPesoMaxAgenda: suggestion.qtPesoMaxAgenda === '0' ? '' : suggestion.qtPesoMaxAgenda,
    }
    if (suggestion.suggestionTypeConsultation == TeleconsultationType.TELECONSULTATION && suggestion.quantityClassificationTelehealth > 0) {
      body.nrMinutoDuracao = suggestion.quantityClassificationTelehealth;
    } else if (suggestion.suggestionTypeConsultation == TeleconsultationType.IN_PERSON && suggestion.quantityClassification > 0) {
      body.nrMinutoDuracao = suggestion.quantityClassification;
    }

    try {
      const response = await HttpService.post<TimeReservationResponse>(
        APPLICATION_ENDPOINTS.TIME_RESERVATION,
        { patientId: PseudonymAppointmentModel.stepOne.currentPatientSelected.id },
        { body: body }
      );
      const message = response.body?.abortMessage;
      if (message) {
        showToastNotificationWarning(message);
        return false;
      }
      return true;
    } catch {
      this.showToastNotificationLoadDataError();
      return false;
    }
  }

  private populateScheduleItem(selectedDate: ScheduleItem | undefined, scheduleItem: ScheduleItem, groupSequence: number): void {
    if (!selectedDate || scheduleItem.groupId !== groupSequence) {
      return;
    }

    scheduleItem.agendaId = selectedDate.agendaId;
    scheduleItem.addressNeighborhood = selectedDate.addressNeighborhood;
    scheduleItem.addressStreet = selectedDate.addressStreet;
    scheduleItem.availableType = selectedDate.availableType;
    scheduleItem.cdEspecialidade = selectedDate.cdEspecialidade;
    scheduleItem.cdEstabelecimento = selectedDate.cdEstabelecimento;
    scheduleItem.cdMedico = selectedDate.cdMedico;
    scheduleItem.dsEstablishment = selectedDate.dsEstablishment;
    scheduleItem.ieAcao = selectedDate.ieAcao;
    scheduleItem.ieReturn = selectedDate.ieReturn;
    scheduleItem.image = selectedDate.image;
    scheduleItem.nrSeqAgenda = String(selectedDate.nrSeqAgenda);
    scheduleItem.nrSeqProcInterno = selectedDate.nrSeqProcInterno;
    scheduleItem.observation = selectedDate.observation;
    scheduleItem.period = selectedDate.period;
    scheduleItem.price = selectedDate.price;
    scheduleItem.priceNoFormatted = selectedDate.priceNoFormatted;
    scheduleItem.quantityClassification = selectedDate.quantityClassification;
    scheduleItem.quantityClassificationTelehealth = selectedDate.quantityClassificationTelehealth;
    scheduleItem.qtPesoMaxAgenda = String(selectedDate.qtPesoMaxAgenda);
    scheduleItem.selectedDate = selectedDate.date;
    scheduleItem.startDate = selectedDate.startDate;
    scheduleItem.subtitle1 = selectedDate.subtitle1;
    scheduleItem.title = selectedDate.title;
    scheduleItem.isShowPrice = selectedDate.isShowPrice;
    scheduleItem.attachedPrescheduleFiles = selectedDate.attachedPrescheduleFiles;
    scheduleItem.appointmentType = selectedDate.appointmentType;
    scheduleItem.shouldDisplayTheConsultationValue = selectedDate.shouldDisplayTheConsultationValue;
    scheduleItem.shouldDisplayTheExamValue = selectedDate.shouldDisplayTheExamValue;
    scheduleItem.nrSeqAgeint = selectedDate.nrSeqAgeint;
    scheduleItem.teleconsultationType = selectedDate.teleconsultationType;
    scheduleItem.suggestionTypeConsultation = selectedDate.suggestionTypeConsultation;
    scheduleItem.ruleClassification = selectedDate.ruleClassification;
    scheduleItem.ruleClassificationTelehealth = selectedDate.ruleClassificationTelehealth;
  }

  private showPseudonymWaitingListModal(integratedScheduleItemSequence?: string): void {
    this.pseudonymSchedulingWaitingListService.showPseudonymWaitingListModal(integratedScheduleItemSequence);
    this.verifyIfCanGoToTheNextStep();
  }

  private removeItemPseudonymWaitingList(integratedScheduleItemSequence: string): void {
    this.pseudonymSchedulingWaitingListService.removeItemPseudonymWaitingList(integratedScheduleItemSequence);
  }

  private removeItemPseudonymSelected(suggestionGroupId: string): void {
    const suggestionCardChoosenListCopy = this.getArrayScheduleItemCopy(this.suggestionCardChoosenList);
    const suggestionValue = suggestionCardChoosenListCopy.filter(item =>
      String(item.suggestionGroupId) === String(suggestionGroupId));
    if (!suggestionValue) {
      return;
    }

    this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);
    this.pseudonymSchedulingWaitingListService.removeItemPseudonymSelected(suggestionGroupId, suggestionValue);
    const choosenItemsData = this.trailChoosenItemsApi?.getData() as unknown as Array<ScheduleItem>;

    this.trailChoosenItemsApi.setLoading(true);
    if (this.panelSuggestionGroupsApi.isVisible()) {
      this.deckSuggestionGroupsApi.setData([]);
      this.deckSuggestionGroupsApi.setDone(true);
    }

    this.panelSuggestionGroupsApi.setVisible(false);
    const newData = choosenItemsData.map(item => {
      item.isSelected = false;
      const suggestion = suggestionValue.find(value => item.nrSeqAgeintItem === value.nrSeqAgeintItem && item.suggestionGroupId === value.suggestionGroupId);
      if (suggestion) {
        PseudonymAppointmentModel.stepSeven.multiSelected = PseudonymAppointmentModel.stepSeven.multiSelected.filter(
          (selected: { nrSeqAgeintItem: string }) => selected.nrSeqAgeintItem.toString() !== suggestion.nrSeqAgeintItem
        );
        suggestion.removeItemSelected = item.removeItemSelected;
        suggestion.isSelected = true;
        return suggestion
      }
      return item
    });

    setTimeout(() => {
      this.trailChoosenItemsApi.setData(newData as unknown as Record<string, Value>[]);
      this.trailChoosenItemsApi.setLoading(false);
      PseudonymAppointmentModel.stepSeven.hasSelected = false;

      const datePickValue = this.datePickerScheduleRange.getInitialDate() as string;
      this.updateDataOnMapperSuggestionGroups(datePickValue);
    }, 1000);
  }

  populateSuggestionTab(): void {
    PseudonymAppointmentModel.stepSeven.suggestions = {} as Suggestions;
    this.suggestionListModel = {} as PseudonymSuggestionListModel;
    this.panelNotificationSuggestionApi.setVisible(false);
    this.panelSuggestionListApi.setVisible(false);
    this.panelLoaderApi.setVisible(true);

    this.getAvaliableSuggestions()
      .finally(() => this.panelLoaderApi.setVisible(false));
  }

  private async getAvaliableSuggestions(): Promise<void> {
    if (this.getIsRescheduleContext()) {
      return this.getSuggestions();
    }

    await this.pseudonymIntegratedScheduleService.getIntegratedScheduleSequenceAndSaveInternalProcedures();
    return await this.getSuggestions();
  }

  private async getSuggestions(): Promise<void> {
    const dataRange = PseudonymAppointmentModel.stepSix.selectedDateRange;
    const location = PseudonymAppointmentModel.stepFive.location;
    this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);
    await HttpService.post(APPLICATION_ENDPOINTS.GET_NEW_APPOINTMENT_SCHEDULE_CONFIG)
      .then(response => {
        const schedulingConfig = response.body as SchedulingConfig;
        const finalDate = schedulingConfig.scheduleRangeAllowed.toString();
        dataRange.start = moment().utc().toISOString();
        dataRange.end = moment(moment.utc().startOf('day')).add(finalDate, 'days').toISOString();
        this.context.set({dateRange: dataRange} as Record<string, Value>);
      });

    let isAnotherPeriod = this.context.get('isAnotherPeriod') as boolean;

    if (isAnotherPeriod) {
      isAnotherPeriod = false;
      this.context.set({ isAnotherPeriod } as Record<string, Value>);
      dataRange.start = PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.start;
      dataRange.end = PseudonymAppointmentModel.stepAnotherPeriod.anotherPeriodRange.end;
    }

    return HttpService.get(APPLICATION_ENDPOINTS.SUGGESTION_DATES, {
      establishmentFilter: PseudonymAppointmentModel.stepOne.appointmentEstablishment.cdEstablishment,
      finalDate: dataRange.end,
      initialDate: dataRange.start,
      locationId: location.nrSequencia,
      nrSeqAgeint: PseudonymAppointmentModel.integratedScheduleSequence,
      patientId: String(PseudonymAppointmentModel.stepOne.currentPatientSelected.id),
    }).then(response => {
      const suggestionGroups = response.body as Array<SuggestionGroups>;
      const suggestions = this.mountSuggestions(suggestionGroups);

      const emptySuggestionsList = [] as Array<ScheduleItem>;
      suggestionGroups.forEach(estabGroup => {
        if (!estabGroup.establishmenSuggestionsGroups || estabGroup.establishmenSuggestionsGroups.length === 0) {
          estabGroup.scheduleItems.forEach(item => {
            if (ScheduleType.CONSULTATION === item.type) {
              PseudonymAppointmentModel.findAnotherPhysician.emptyEstablishmentGroups = true;
              emptySuggestionsList.push(item);
            }
          });
        }
      });

      PseudonymAppointmentModel.findAnotherPhysician.emptySuggestions = emptySuggestionsList;

      setTimeout(() => {
        this.panelNotificationSuggestionApi.setVisible(
          suggestions.isShowNotificationWaitingList &&
          suggestions.isEnabledToScheduleWaitingList &&
          !this.getIsRescheduleContext()
        );
        this.panelSuggestionListApi.setVisible(suggestions.suggestionGroups.length > 0);
        this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, !this.panelSuggestionListApi.isVisible);
        PseudonymAppointmentModel.stepSeven.suggestions = suggestions;
      });
    });
  }

  private mountSuggestions(suggestionGroups: Array<SuggestionGroups>): Suggestions {
    const suggestions = {} as Suggestions;
    suggestions.suggestionGroups = suggestionGroups;
    suggestions.suggestionList = [];
    suggestions.blockedSuggestionList = [];
    suggestions.isEnabledToScheduleWaitingList = this.isEnabledToScheduleWaitingList();

    this.populateSuggestionGroups(suggestions.suggestionGroups);

    suggestions.suggestionGroups.forEach(group => {
      if (!suggestions.isShowNotificationWaitingList) {
        const hasEstablishmentSuggestionsGroup = group.establishmenSuggestionsGroups?.length > 0;
        const hasBlockedItem = group.blockedItems?.length > 0;
        suggestions.isShowNotificationWaitingList = hasEstablishmentSuggestionsGroup && hasBlockedItem;
      }

      this.populateSuggestionList(
        suggestions.suggestionList,
        group.scheduleItems,
        group.groupId,
        group.establishmenSuggestionsGroups,
        suggestions.isEnabledToScheduleWaitingList
      );
      this.populateBlockedSuggestionList(suggestions.blockedSuggestionList, group.blockedItems);
    });

    if (suggestionGroups[0].quizMessageAbort) {
      showToastNotificationWarning(suggestionGroups[0].quizMessageAbort, 27000);
    }

    return suggestions;
  }

  private populateSuggestionGroups(suggestionGroups: Array<SuggestionGroups>): void {
    const isShowPrice = this.isShowSchedulePrice();

    suggestionGroups.forEach((group, index) => {
      group.groupId = index;
      group.nrSeqAgeint = PseudonymAppointmentModel.integratedScheduleSequence;
      group.isShowPrice = isShowPrice;
    });
  }

  private populateSuggestionList(
    suggestionList: Array<ScheduleItem>,
    scheduleItems: Array<ScheduleItem>,
    groupSequence: number,
    establishmentSuggestionsGroups: Array<EstablishmenSuggestions>,
    isEnabledToScheduleWaitingList: boolean): void {
    if (!scheduleItems?.length) {
      return;
    }

    const hasSchedulingTimes = establishmentSuggestionsGroups.length > 0;
    scheduleItems.forEach(item => {
      suggestionList.push({
        agendaId: '',
        availableType: item.availableType,
        cdEspecialidade: item.cdEspecialidade || '',
        cdEstabelecimento: item.cdEstabelecimento,
        cdMedico: item.cdMedico || '',
        copagoPrice: '0',
        copagoPriceNoFormatted: 0,
        date: item.date,
        dsEstablishment: '',
        dsProcInterno: item.dsProcInterno,
        endDate: '',
        groupId: groupSequence,
        hasSchedulingTimes: hasSchedulingTimes,
        ieAcao: '',
        ieReturn: item.ieReturn,
        image: item.image,
        isEnabledToScheduleWaitingList,
        isItemFromReschedule: this.getIsRescheduleContext(),
        isShowPrice: item.isShowPrice,
        key: item.key,
        nrMinutoDuracao: 0,
        nrSeqAgeint: item.nrSeqAgeint,
        nrSeqAgeintItem: item.nrSeqAgeintItem,
        nrSeqAgenda: '',
        nrSeqProcInterno: item.nrSeqProcInterno,
        observation: item.observation,
        period: item.period,
        price: '0',
        priceNoFormatted: 0,
        qtPesoMaxAgenda: '',
        quantityClassification: 0,
        quantityClassificationTelehealth: 0,
        ruleClassification: '',
        ruleClassificationTelehealth: '',
        selectedDate: '',
        shouldDisplayTheConsultationValue: NO,
        shouldDisplayTheExamValue: NO,
        startDate: '',
        subtitle1: item.subtitle1,
        suggestionGroupId: groupSequence,
        suggestionId: item.suggestionId,
        suggestionTypeConsultation: item.suggestionTypeConsultation,
        title: item.title,
        type: item.type,
      });
    });
  }

  private populateBlockedSuggestionList(blockedSuggestionList: Array<ScheduleItem>, blockedItems: Array<BlockedItems>): void {
    if (!blockedItems?.length) {
      return;
    }

    blockedItems.forEach(item => {
      blockedSuggestionList.push({
        cdEspecialidade: item.cdEspecialidade || '',
        dsEstablishment: '',
        groupId: -1,
        isShowPrice: false,
        nrSeqProcInterno: item.nrSeqProcInterno || '',
        selectedDate: '',
        title: item.dsItemGridAgeint,
        type: item.ieTipoAgendamento
      } as ScheduleItem);
    });
  }

  private isEnabledToScheduleWaitingList(): boolean {
    const isWaitingListConsultationEnabled = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_CONSULTA) === YES;
    const isWaitingListExamEnabled = JWTTokenUtils.getClaimValue(TokenConstants.IE_LISTA_ESP_EXAME) === YES;
    return isWaitingListConsultationEnabled && isWaitingListExamEnabled;
  }

  private isShowSchedulePrice(): boolean {
    const healthInsurance = PseudonymAppointmentModel.stepOne.healthInsurance;
    return Boolean(healthInsurance.isPrivateInsurance === 'S' || healthInsurance.isPrivate);
  }

  private filterSuggestionWaitingList(item: ScheduleItem): boolean {
    if(item.isItemFromReschedule || item.hasSchedulingTimes || !item.isEnabledToScheduleWaitingList) {
      item.removeItemSelected = this.removeItemPseudonymSelected.bind(this);
      return false;
    }

    const scheduleWaitingListDataArray = PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray;
    item.isWaitingListItem = scheduleWaitingListDataArray.some(row => Number(row.nrSeqAgeintItem) === Number(item.nrSeqAgeintItem));
    item.showWaitingListModal = this.showPseudonymWaitingListModal.bind(this);
    item.removeItemFromWaitingList = this.removeItemPseudonymWaitingList.bind(this);

    return true;
  }

  private updateCardsChoosenItems(): void {
    if (!this.panelChoosenItemsApi.isVisible()) {
      return;
    }

    const choosenItemsData = this.trailChoosenItemsApi?.getData() as unknown as Array<ScheduleItem>;
    const suggestionItemsSelected = this.suggestionListModel.suggestionItemsSelected;
    const suggestionItems = suggestionItemsSelected.map(element => element.suggestionItems);
    const multiselected = PseudonymAppointmentModel.stepSeven.multiSelected;

    this.trailChoosenItemsApi.setLoading(true);
    this.trailChoosenItemsApi.setData([]);

    setTimeout(() => {
      choosenItemsData.forEach(item => {
        suggestionItems.forEach(suggestion => {
          suggestion.forEach(suggestionItem => {
            if (item.nrSeqAgeintItem === suggestionItem.nrSeqAgeintItem.toString()){
              item.dsEstablishment = suggestionItem.dsEstablishment;
              item.startDate = suggestionItem.startDate;
              item.isShowPrice = suggestionItem.isShowPrice;
              item.price = suggestionItem.price;
              multiselected.push({
                nrSeqAgeintItem: suggestionItem.nrSeqAgeintItem,
                suggestionGroupId: suggestionItem.suggestionGroupId
              });
            }
          });
        });
      });

      this.trailChoosenItemsApi.setData(choosenItemsData as unknown as Record<string, Value>[]);
      this.trailChoosenItemsApi.setLoading(false);
    });
  }

  private updateSelectedCardChoosenItems(choosenItemsData: Array<ScheduleItem>, integratedScheduleItemSequence: string): void {
    if (!this.panelChoosenItemsApi.isVisible()) {
      return;
    }

    this.trailChoosenItemsApi.setLoading(true);
    this.trailChoosenItemsApi.setData([]);

    setTimeout(() => {
      choosenItemsData.forEach(item => {
        item.isSelected = item.isSelected = item.nrSeqAgeintItem === integratedScheduleItemSequence;
        if(item.isItemFromReschedule || item.hasSchedulingTimes || !item.isEnabledToScheduleWaitingList) {
          item.removeItemSelected = this.removeItemPseudonymSelected.bind(this);
        } else {
          const scheduleWaitingListDataArray = PseudonymAppointmentModel.waitingList.scheduleWaitingListDataArray;
          item.isWaitingListItem = scheduleWaitingListDataArray.some(row => Number(row.nrSeqAgeintItem) === Number(item.nrSeqAgeintItem));
          item.showWaitingListModal = this.showPseudonymWaitingListModal.bind(this);
          item.removeItemFromWaitingList = this.removeItemPseudonymWaitingList.bind(this);
        }
      });

      this.trailChoosenItemsApi.setData(choosenItemsData as unknown as Record<string, Value>[]);
      this.trailChoosenItemsApi.setLoading(false);
    });
  }

  private getSuggestionsOrdered(suggestions: Array<EstablishmenSuggestions>, ordenationId: string): Array<EstablishmenSuggestions> {
    switch (ordenationId) {
      case FilterSuggestionType.AMOUNT: {
        return suggestions.sort((first, second) => {
          const resultComparePrices = this.compareSuggestionsPrices(first, second);
          const resultCompareStartDates = this.compareSuggestionsStartDates(first, second);
          return resultComparePrices === 0 ? resultCompareStartDates : resultComparePrices;
        });
      }
      case FilterSuggestionType.DURATION: {
        return suggestions.sort((first, second) => {
          const resultCompareSortingLogicOfTypeDuration = this.compareSortingLogicOfTypeDuration(first, second);
          const resultCompareStartDates = this.compareSuggestionsStartDates(first, second);
          return resultCompareSortingLogicOfTypeDuration === 0 ? resultCompareStartDates : resultCompareSortingLogicOfTypeDuration;
        });
      }
      case FilterSuggestionType.TIME: {
        return suggestions.sort((first, second) => {
          const resultCompareStartDates = this.compareSuggestionsStartDates(first, second);
          const resultCompareSortingLogicOfTypeDuration = this.compareSortingLogicOfTypeDuration(first, second);
          return resultCompareStartDates === 0 ? resultCompareSortingLogicOfTypeDuration : resultCompareStartDates;
        });
      }
      default: {
        return [];
      }
    }
  }

  private compareSuggestionsPrices(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstPriceValues = this.getSuggestionsPrices(first.suggestions);
    const secondPriceValues = this.getSuggestionsPrices(second.suggestions);
    const firstMinPriceValue = Math.min(...firstPriceValues);
    const secondMinPriceValue = Math.min(...secondPriceValues);
    return this.filterSuggestionComparator(firstMinPriceValue, secondMinPriceValue);
  }

  private getSuggestionsPrices(suggestions: Array<SuggestionItems>): Array<number> {
    return suggestions.reduce<Array<number>>((result, value) => {
      const suggestItemsAmounts = value.suggestionItems.map(item => item.priceNoFormatted);
      result.push(Math.min(...suggestItemsAmounts));
      return result;
    }, []);
  }

  private compareSuggestionsStartDates(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstStartDateTimes = this.getSuggestionStartDates(first);
    const secondStartDateTimes = this.getSuggestionStartDates(second);
    const firstMinStartDate = Math.min(...firstStartDateTimes);
    const secondMinStartDate = Math.min(...secondStartDateTimes);
    return this.filterSuggestionComparator(firstMinStartDate, secondMinStartDate);
  }

  private getSuggestionStartDates(establishmentSuggestion: EstablishmenSuggestions): Array<number> {
    return establishmentSuggestion.events.map(value => moment.utc(value.startDate).unix());
  }

  private compareSortingLogicOfTypeDuration(first: EstablishmenSuggestions, second: EstablishmenSuggestions): number {
    const firstDuration = this.extractNumberFromString(first.filterParameter);
    const secondDuration = this.extractNumberFromString(second.filterParameter);
    return this.filterSuggestionComparator(firstDuration, secondDuration);
  }

  private extractNumberFromString(value?: string): number {
    return Number(value?.replace(/[^0-9]/g, '') ?? 0);
  }

  private filterSuggestionComparator(firstItem: number, secondItem: number): number {
    if (firstItem < secondItem) { return -1; }
    if (firstItem > secondItem) { return 1; }
    return 0;
  }

  private doTeleconsultationFilter(suggestionList: Array<EstablishmenSuggestions>, onlyTeleconsultation: boolean): Array<EstablishmenSuggestions> {
    const newSuggestionsCard = suggestionList
      .map(suggestionCard => {
        const suggestionItems = suggestionCard.suggestions;
        const suggestionItemsFiltered = this.doFilterSuggestionItems(suggestionItems, onlyTeleconsultation);

        return suggestionItemsFiltered.length > 0
          ? { ...suggestionCard, suggestions: suggestionItemsFiltered }
          : null;
      })
      .filter(value => value != null) as Array<EstablishmenSuggestions>;

    return newSuggestionsCard;
  }

  private doFilterSuggestionItems(suggestionItems: Array<SuggestionItems>, onlyTeleconsultation: boolean): Array<SuggestionItems> {
    const newSuggestionItems: Array<SuggestionItems> = [];
    suggestionItems.forEach(suggestion => {
      const suggestionFiltered = suggestion.suggestionItems.filter(item => {
        return item.teleconsultationType === TeleconsultationType.TELECONSULTATION ||
          item.teleconsultationType === ScheduleAvailableType.HIBRID && onlyTeleconsultation
      });

      if (suggestionFiltered.length > 0) {
        newSuggestionItems.push(suggestion);
      }
    });

    return newSuggestionItems;
  }

  private verifyIfCanGoToTheNextStep(): void {
    const suggestionList = PseudonymAppointmentModel.stepSeven.suggestions.suggestionList;
    const suggestionWaitingList = suggestionList.filter(item => this.filterSuggestionWaitingList(item));

    if (suggestionList.length === suggestionWaitingList.length) {
      this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, true);
    }
  }

  private unSelectAllCards(): void {
    if (this.panelSuggestionGroupsApi.isVisible()){
      this.deckSuggestionGroupsApi.unselectAll();
      const suggestionItems = this.suggestionListModel.suggestionItemsSelected;
      const selectedDate = moment(suggestionItems[0].suggestionItems[0].date).format('YYYY-MM-DD');
      const datePickerValue = moment(this.datePickerScheduleRange.getValue()).format('YYYY-MM-DD');
      if (selectedDate === datePickerValue) {
        if (Number(suggestionItems[0]?.suggestionId) > 1) {
          this.handleOnMountDeckSuggestionItems({
            target: this.deckSuggestionGroupsItemsApi
          } as unknown as MountEvent);
        } else {
          this.deckSuggestionGroupsItemsApi.unselectAll();
        }
      }
      this.suggestionListModel.hasSelected = false;
      this.suggestionListModel.wantToRequest = false;
      this.suggestionListModel.suggestionItemsSelected = [];
      this.suggestionListModel.selectedItemList = [];
      this.suggestionListModel.suggestionGroup.scheduleItems[0].suggestionSelected = false;
      this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);
    }
  }

  private getFirstAvailableSuggestionGroup(suggestionGroups: Array<SuggestionGroups>): SuggestionGroups | undefined {
    if (suggestionGroups?.length == 0) {
      return {} as SuggestionGroups;
    }

    return suggestionGroups.find(item => item.establishmenSuggestionsGroups?.length > 0);
  }

  private getArrayScheduleItemCopy(arrayScheduleItem: Array<ScheduleItem>): Array<ScheduleItem> {
    const jsonObject = JSON.stringify(arrayScheduleItem);
    return JSON.parse(jsonObject) as Array<ScheduleItem>;
  }

  private verifyIfCanHandleConfirmSuggestion(): void {
    const allowSelectDesiredDate = PseudonymAppointmentModel.stepSix.allowSelectDesiredDate as boolean;
    allowSelectDesiredDate ? this.openAskForDesiredDateDialog() : this.handleConfirmSuggestion();
  }

  private changePanelSuggestionGroupsApi(visibility: boolean): void {
    if (this.panelSuggestionGroupsApi.isVisible()) {
      this.deckSuggestionGroupsApi.setData([]);
      this.deckSuggestionGroupsApi.setDone(!visibility);
    }

    this.panelSuggestionGroupsApi.setVisible(visibility);
  }

  private openAskForDesiredDateDialog(): void {
    const desiredDateCustomMessage = this.context.get('desiredDateCustomMessage') as string;
    ModalService.openDialog({
      closeable: false,
      dialogDescription: desiredDateCustomMessage || 'patient.appoitment.correct.date',
      dockButtons: [
        {
          action: () => this.openModalToSelectDesiredAppointmentDate(),
          title: MessageService.translate('patient.modal.button-cancel'),
          type: ModalButtonType.CANCEL
        },
        {
          action: () => this.handleAfterSubmitSuggestion(),
          title: MessageService.translate('patient.moda.button-yes'),
          type: ModalButtonType.SUCCESS
        }
      ],
      title: 'patient.appointment.confirm.title'
    });
  }

  private openModalToSelectDesiredAppointmentDate(): void {
    const data = this.suggestionListModel.suggestionGroup as SuggestionGroups;
    const modalProps: ModalProps = {
      className: FragmentNames.APPOINTMENT_CREATION_CHOOSE_ANOTHER_DATE,
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: FragmentNames.PSEUDONYM_SUGGESTION_CHOOSE_ANOTHER_DATE,
      noPadding: true,
      onSubmit: evt => this.handleSubmitDesiredDate(evt, data),
      title: '',
      uuid: '',
    }

    isMobile() ? ModalService.openFullscreen(modalProps) : ModalService.openDefault(modalProps);
  }

  private handleSubmitDesiredDate(submitEvent: SubmitEvent, data: SuggestionGroups): void {
    const desiredAppointmentDate = submitEvent.record as DesiredAppointmentDate;
    const desiredDate = this.getDesiredDate(desiredAppointmentDate.date, desiredAppointmentDate.time.replaceAll('Z', ':00Z'));
    const selectedItems = this.suggestionListModel.suggestionItemsSelected;

    if (this.suggestionListModel.hasSelected && selectedItems?.length > 0) {
      selectedItems.forEach(item => {
        item.suggestionItems.forEach(suggestionItem => {
          suggestionItem.date = desiredDate;
          const body = {
            desiredDate: desiredDate,
            scheduleType: suggestionItem.appointmentType,
            sequenceIntegratedSchedule: data.nrSeqAgeint,
            sequenceNumber: suggestionItem.nrSeqAgeintItem
          };

          HttpService.post(APPLICATION_ENDPOINTS.DESIRED_DATE, {}, { body });
        });
      });
    }

    this.handleAfterSubmitSuggestion();
  }

  private handleAfterSubmitSuggestion(): void {
    this.handleConfirmSuggestion()
      .then(() => showToastNotificationInformation(MessageService.translate('patient.desired.date.acknowledgment')));
  }

  private async handleConfirmSuggestion(): Promise<void> {
    const selectedItems = this.suggestionListModel.suggestionItemsSelected;
    let hasGroupingWithRequest = false;

    const suggestionItems = selectedItems[0].suggestionItems;
    if (this.suggestionListModel.hasSelected && selectedItems[0]) {
      if (suggestionItems?.length > 1) {
        const suggestionItem = suggestionItems.find(item => item.availableType === ScheduleAvailableType.REQUEST);
        hasGroupingWithRequest = Boolean(suggestionItem);
      }

      try {
        await this.checkNecessaryConfirmationItems(selectedItems, hasGroupingWithRequest);
        return this.submittableFragmentContext.submit(new SubmitEvent({ record: this.suggestionListModel.selectedItemList }));
      } catch (text) {
        return this.openDialogHasAppointment(text as string);
      }
    }

    return Promise.resolve();
  }

  private checkNecessaryConfirmationItems(selectedItems: Array<SuggestionItems>, hasGroupingWithRequest: boolean): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const suggestionItems = selectedItems[0].suggestionItems;
      const confirmWantRequestPromises: Array<Promise<void>> = [];
      let selectedItemList = this.suggestionListModel.selectedItemList;

      suggestionItems.forEach(suggestion => {
        if (suggestion.availableType === ScheduleAvailableType.REQUEST && !this.suggestionListModel.wantToRequest) {
          const promise = this.confirmWantToRequest(suggestion)
            .catch(() => {
              this.suggestionListModel.lastDeckApi?.unselectAll();
              this.suggestionListModel.hasSelected = false;
              this.suggestionListModel.suggestionItemsSelected = [];
              PseudonymAppointmentModel.stepSeven.suggestionSelected = [];
              this.setWizardStepDone(AppointmentsSteps.STEP_7_SUGGESTION, false);
              reject(new Error('User did not confirm want to request'));
            });

          confirmWantRequestPromises.push(promise);
        } else {
          selectedItemList = selectedItemList.filter(item=> item.nrSeqAgeintItem != suggestion.nrSeqAgeintItem);
          selectedItemList.push(suggestion);
        }
      });

      Promise.all(confirmWantRequestPromises)
        .then(() => {
          if (!hasGroupingWithRequest) {
            const suggestionGroup = this.suggestionListModel.suggestionGroup;
            const selectedSchedules = selectedItemList.filter(item => item.availableType !== ScheduleAvailableType.REQUEST);
            this.setSelectedSuggestion(suggestionGroup.groupId, selectedSchedules);
          }
          this.suggestionListModel.selectedItemList = selectedItemList;
          resolve();
        })
        .catch(() => reject());
    });
  }

  private confirmWantToRequest(suggestion: ScheduleItem): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const modalId = 'b86ad408-df95-44db-a786-eccff7b2053d';
      ModalService.close(modalId);
      ModalService.openDialog({
        closeable: false,
        dialogDescription: MessageService.translate('patient.need-to-confirm'),
        dockButtons: [
          {
            action: () => {
              this.suggestionListModel.wantToRequest = false;
              reject();
            },
            title: 'patient.cancel',
            type: ModalButtonType.CANCEL,
          },
          {
            action: () => {
              this.availableTypeModal(suggestion)
                .then(() => {
                  const suggestionGroup = this.suggestionListModel.suggestionGroup;
                  const selectedItemList = this.suggestionListModel.suggestionItemsSelected[0].suggestionItems;
                  this.setSelectedSuggestion(suggestionGroup.groupId, selectedItemList);
                  selectedItemList[0].suggestionSelected = true;
                  this.suggestionListModel.wantToRequest = true;
                  resolve();
                });
            },
            title: 'patient.confirm',
            type: ModalButtonType.SUCCESS,
          }
        ],
        id: modalId,
        title: 'tws.appointment-suggestion.suggestion',
      });
    });
  }

  private availableTypeModal(suggestion: ScheduleItem): Promise<void> {
    return new Promise<void>((resolve) => {
      ModalService.openDefault({
        closeable: false,
        contextData: { data: suggestion as unknown as Record<string, string> },
        dockButtons: [],
        fragment: FragmentNames.PSEUDONYM_SUGGESTION_CHECK_AVAILABLE,
        onSubmit: (event) => {
          const selectedPeriod = event.record as SuggestionPeriodItem;
          suggestion.observation = selectedPeriod.observation;
          suggestion.period = selectedPeriod.period;
          suggestion.attachedPrescheduleFiles = selectedPeriod.attachedPrescheduleFiles;

          const selectedItemList = this.suggestionListModel.selectedItemList;
          selectedItemList.push(suggestion);
          this.suggestionListModel.selectedItemList = selectedItemList;

          resolve();
        },
        title: '',
      });
    });
  }

  private openDialogHasAppointment(text: string): void {
    ModalService.openDialog({
      closeable: true,
      dialogDescription: text,
      dockButtons: [
        {
          action: () => { return },
          title: 'patient.confirm',
          type: ModalButtonType.SUCCESS,
        },
      ],
      title: 'patient.process.aborted',
    });
  }

  private getDesiredDate(desiredDate: MomentType, desiredTime: string): string {
    const date = moment(desiredDate).utc().format('YYYY-MM-DD');

    return date.concat('T').concat(desiredTime);
  }

  private setSuggestionGroupsMessageOnPanelChoosenItemsText(suggestionGroups: SuggestionGroups | undefined): void {
    if (!suggestionGroups?.scheduleItems || suggestionGroups.scheduleItems.length <= 1) {
      this.panelChoosenItemsTextApi.setVisible(false);
      return;
    }

    this.panelChoosenItemsTextApi.setVisible(true);
  }

  private updateDatePicker(groupId: number,  suggestionGroups: Array<SuggestionGroups>): void {
    if (!suggestionGroups || suggestionGroups.length === 0) {
      return;
    }

    suggestionGroups.forEach(item => {
      if (item.groupId === groupId && item.establishmenSuggestionsGroups.length > 0) {
        this.insertRangeIntoDatePicker(item.establishmenSuggestionsGroups);
      }
    });
  }

  private insertRangeIntoDatePicker(establishmenSuggestionsGroups: EstablishmenSuggestions[]): void {
    if (!establishmenSuggestionsGroups || establishmenSuggestionsGroups.length === 0) {
      return;
    }

    const mapDatePicker = new Map();
    this.datePickerScheduleRange.clearRanges();
    establishmenSuggestionsGroups.forEach(suggestions => {
      if (!mapDatePicker.has(suggestions.value)) {
        this.datePickerScheduleRange.addRange(suggestions.value, suggestions.value);
        if(mapDatePicker.size === 1 && this.datePickerScheduleRange.getInitialDate() != suggestions.value) {
          this.datePickerScheduleRange.setInitialDate(mapDatePicker.keys().next().value);
        }
        mapDatePicker.set(suggestions.value, true);
      }
    });
  }

  private openAnotherPeriodModal(modalId: string): void {
    const onModalClose = (): void => {
      const hasValues = PseudonymAppointmentModel.stepAnotherPeriod.hasValues;
      if (!hasValues) {
        return;
      }
      PseudonymAppointmentModel.stepAnotherPeriod.hasValues = false;
      this.panelNotificationSuggestionApi.setVisible(false);
      this.panelSuggestionListApi.setVisible(false);
      this.populateSuggestionTab();
    };
    if (!isMobile()) {
      ModalService.openDefault({
        closeable: true,
        contextData: { modalId: modalId } as Record<string, Value>,
        fragment: FragmentNames.ANOTHER_PERIOD,
        id: modalId,
        onClose: onModalClose,
        title: 'tws.date-field.select-date',
      });
    } else {
      ModalService.openDialog({
        closeable: true,
        contextData: { modalId: modalId } as Record<string, Value>,
        fragment: FragmentNames.ANOTHER_PERIOD,
        id: modalId,
        onClose: onModalClose,
        title: 'tws.date-field.select-date',
      });
    }
  }

  private setDataTrailMapper(suggestionGroups: SuggestionGroups[], targetDate: string): Array<unknown> {
    const message = MessageService.translate('tws.appointment-suggestion.suggestions');
    const establishmentsSuggestions = suggestionGroups.flatMap(
      group => Array.isArray(group.establishmenSuggestionsGroups) ? group.establishmenSuggestionsGroups : []
    );

    if (establishmentsSuggestions.length === 0) {
      this.panelEstablishmentApi.setVisible(false);
      this.panelTitleEstablishmentApi.setVisible(false);
      return [];
    }

    const establishmentMap = new Map<string, { count: number; dsGrupoEstab: string; nrSequencia: string; suggestion: string;  }>();

    establishmentsSuggestions.forEach(establishment => {
      const key = String(establishment.cdEstabelecimento);
      if (!establishmentMap.has(key)) {
        establishmentMap.set(key, {
          count: 0,
          dsGrupoEstab: establishment.dsEstablishment,
          nrSequencia: key,
          suggestion: '',
        });
      }
      if (establishment.value === targetDate) {
        const establishmentEntry = establishmentMap.get(key);
        if (establishmentEntry) {
          establishmentEntry.count += 1;
          establishmentEntry.suggestion = `${establishmentEntry.count} ${message}`;
        }
      }
    });

    return Array.from(establishmentMap.values()).filter(est => est.count > 0);
  }

  private selectedSuggestion(hasSelected: boolean, selectedItems: Array<SuggestionItems>, deckApi: DeckAPI, suggestionsHybrid: ScheduleItem[]): void {
    const handleHybridOrConfirm = (hybridSuggestions: ScheduleItem[]) => {
      if (hybridSuggestions.length) {
        this.openDialogHasConfirmTypeConsultation();
      } else {
        this.verifyIfCanHandleConfirmSuggestion();
      }
    };

    if (hasSelected && selectedItems?.length > 0) {
      this.suggestionListModel.suggestionItemsSelected = selectedItems;
    }

    const suggestionGroup = this.suggestionListModel.suggestionGroup;
    const selectedItemList = selectedItems[0].suggestionItems;

    this.setSelectedSuggestion(suggestionGroup.groupId, selectedItemList).then(() => {
      if (PseudonymAppointmentModel.stepSeven.isLastSuggestionDenied) {
        PseudonymAppointmentModel.stepSeven.isLastSuggestionDenied = false;
        deckApi.unselectAll();
        return;
      }
      handleHybridOrConfirm(suggestionsHybrid);
    });
  }

  refreshTrailChoosenItems(): void {
    if (this.trailChoosenItemsApi?.setData) {
      this.trailChoosenItemsApi.setData(
        PseudonymAppointmentModel.stepSeven.suggestions.suggestionList as unknown as Record<string, Value>[]
      );
    }
  }
}
